import { useRef } from 'react';
import type { FormFeedback } from '@/types';
import styles from './FormMessage.module.css';

export interface FormMessageProps {
  readonly feedback: FormFeedback | null;
}

/**
 * Inline form result.
 *
 * Always mounted as a live region so the outcome is announced when it appears;
 * mounting the region only alongside the message would leave some screen
 * readers silent because the region did not exist when the text arrived.
 */
export function FormMessage({ feedback }: FormMessageProps) {
  // A live region is announced when its content *changes in the DOM*. Two
  // consecutive submits that fail the same way render identical text, which is
  // no change at all — so the second attempt would pass silently. Keying the
  // paragraph on each new feedback object forces a remount, and with it an
  // announcement, without altering what is displayed.
  const announcement = useRef(0);
  const lastSeen = useRef<FormFeedback | null>(null);

  if (feedback !== lastSeen.current) {
    lastSeen.current = feedback;
    announcement.current += 1;
  }

  return (
    <div role="status" aria-live="polite" aria-atomic="true">
      {feedback ? (
        <p
          key={announcement.current}
          className={`${styles.message} ${
            feedback.tone === 'error' ? styles.error : styles.success
          }`}
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}
